import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
  ScreenShare as ScreenShareIcon,
  StopScreenShare as StopScreenShareIcon,
  CallEnd as CallEndIcon,
  Chat as ChatIcon,
  People as PeopleIcon,
  PanTool as HandIcon,
  MoreVert as MoreIcon,
  Settings as SettingsIcon,
  FiberManualRecord as RecordIcon,
  Stop as StopIcon
} from '@mui/icons-material';

function MeetingControls({
  isMuted,
  isCameraOff,
  isScreenSharing,
  isHandRaised,
  isRecording,
  isHost,
  unreadChatCount = 0,
  onToggleMute,
  onToggleCamera,
  onToggleScreenShare,
  onToggleHand,
  onToggleChat,
  onToggleParticipants,
  onToggleSettings,
  onStartRecording,
  onStopRecording,
  onEndMeeting,
  onLeaveMeeting
}) {
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);

  const handleMoreClick = (event) => {
    setMoreMenuAnchor(event.currentTarget);
  };

  const handleMoreClose = () => {
    setMoreMenuAnchor(null);
  };

  const handleRecordingToggle = () => {
    handleMoreClose();
    if (isRecording) {
      onStopRecording?.();
    } else {
      onStartRecording?.();
    }
  };

  const handleEndMeeting = () => {
    handleMoreClose();
    onEndMeeting?.();
  };

  const handleLeaveMeeting = () => {
    handleMoreClose();
    onLeaveMeeting?.();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2,
        p: 2,
        bgcolor: '#2a2a2a',
        borderTop: '1px solid #444'
      }}
    >
      {/* Main Controls */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        {/* Microphone */}
        <Tooltip title={isMuted ? 'Unmute' : 'Mute'}>
          <IconButton
            onClick={onToggleMute}
            sx={{
              bgcolor: isMuted ? 'error.main' : 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              '&:hover': {
                bgcolor: isMuted ? 'error.dark' : 'rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            {isMuted ? <MicOffIcon /> : <MicIcon />}
          </IconButton>
        </Tooltip>

        {/* Camera */}
        <Tooltip title={isCameraOff ? 'Turn on camera' : 'Turn off camera'}>
          <IconButton
            onClick={onToggleCamera}
            sx={{
              bgcolor: isCameraOff ? 'error.main' : 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              '&:hover': {
                bgcolor: isCameraOff ? 'error.dark' : 'rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            {isCameraOff ? <VideocamOffIcon /> : <VideocamIcon />}
          </IconButton>
        </Tooltip>

        {/* Screen Share */}
        <Tooltip title={isScreenSharing ? 'Stop sharing' : 'Share screen'}>
          <IconButton
            onClick={onToggleScreenShare}
            sx={{
              bgcolor: isScreenSharing ? 'primary.main' : 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              '&:hover': {
                bgcolor: isScreenSharing ? 'primary.dark' : 'rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            {isScreenSharing ? <StopScreenShareIcon /> : <ScreenShareIcon />}
          </IconButton>
        </Tooltip>

        {/* Raise Hand */}
        <Tooltip title={isHandRaised ? 'Lower hand' : 'Raise hand'}>
          <IconButton
            onClick={onToggleHand}
            sx={{
              bgcolor: isHandRaised ? 'warning.main' : 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              '&:hover': {
                bgcolor: isHandRaised ? 'warning.dark' : 'rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            <HandIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Secondary Controls */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        {/* Participants */}
        <Tooltip title="Participants">
          <IconButton
            onClick={onToggleParticipants}
            sx={{
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            <PeopleIcon />
          </IconButton>
        </Tooltip>

        {/* Chat */}
        <Tooltip title="Chat">
          <IconButton
            onClick={onToggleChat}
            sx={{
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            <Badge badgeContent={unreadChatCount} color="error">
              <ChatIcon />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* Settings */}
        {onToggleSettings && (
          <Tooltip title="Settings">
            <IconButton
              onClick={onToggleSettings}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)'
                }
              }}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        )}

        {/* More options */}
        <Tooltip title="More options">
          <IconButton
            onClick={handleMoreClick}
            sx={{
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            <MoreIcon />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={moreMenuAnchor}
          open={Boolean(moreMenuAnchor)}
          onClose={handleMoreClose}
        >
          {isHost && (
            <>
              <MenuItem onClick={handleRecordingToggle}>
                <ListItemIcon>
                  {isRecording ? <StopIcon /> : <RecordIcon />}
                </ListItemIcon>
                <ListItemText>
                  {isRecording ? 'Stop Recording' : 'Start Recording'}
                </ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleEndMeeting}>
                <ListItemIcon>
                  <CallEndIcon color="error" />
                </ListItemIcon>
                <ListItemText>End Meeting for All</ListItemText>
              </MenuItem>
              <Divider />
            </>
          )}
          <MenuItem onClick={handleLeaveMeeting}>
            <ListItemIcon>
              <CallEndIcon />
            </ListItemIcon>
            <ListItemText>Leave Meeting</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {/* End Call Button */}
      <Tooltip title={isHost ? 'End meeting' : 'Leave meeting'}>
        <IconButton
          onClick={isHost ? onEndMeeting : onLeaveMeeting}
          sx={{
            bgcolor: 'error.main',
            color: 'white',
            '&:hover': {
              bgcolor: 'error.dark'
            }
          }}
        >
          <CallEndIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export default MeetingControls;
